import { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { AccountManager, AppInsights } from '@bluedolphin/util-module-mf';
import { useSnackbar } from '@bluedolphin/ui';
import { useTranslation } from 'react-i18next';

import { useUser } from '../../../providers/user';
import { TrackingKeys } from '../../../util/tracking-keys';
import { useApplication } from '../../../providers/application';
import { routePaths } from '../../molecules/DisruptiveErrorModal';

/**
 * NetworkHandler
 */
export default function NetworkHandler() {
    const { user } = useUser();
    const { pathname } = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { isErrorModalVisible, toggleErrorModal } = useApplication();

    const routeMatch = useMemo(() => {
        let matchFound = null;

        routePaths.forEach((route) => {
            if (matchPath(pathname, route)) {
                matchFound = route;
            }
        });

        return matchFound;
    }, [isErrorModalVisible, pathname]);

    useEffect(() => {
        addEventListeners();
        return () => removeEventListeners();
    }, []);

    useEffect(() => {
        const rootEl = document.getElementById('@bluedolphin/auth-mf');

        if (routeMatch?.navbar) {
            rootEl.classList.add('network-snackbar--navbar');
        } else if (routeMatch) {
            rootEl.classList.remove('network-snackbar--navbar');
        }
    }, [routeMatch?.path]);

    /**
     * handle visibility change
     */
    function onVisibilitychange() {
        if (
            document.visibilityState === 'visible' &&
            window.navigator.onLine &&
            isErrorModalVisible
        ) {
            onBrowserOnline();
        }
    }

    /**
     * handle browser being online
     */
    function onBrowserOffline() {
        toggleErrorModal({
            isVisible: true,
            description: t('internetConnection.lost'),
        });
        AccountManager.setItem('activeInternetDownTime', {
            startTime: new Date(),
            user: { email: user?.email ?? '', id: user?.id ?? '' },
        });
    }

    /**
     * handle browser being offline
     */
    function onBrowserOnline() {
        const activeInternetDownTime = AccountManager.getItem(
            'activeInternetDownTime'
        );

        if (activeInternetDownTime?.startTime) {
            activeInternetDownTime.endTime = new Date();
            AppInsights.trackEvent(
                TrackingKeys.AuthUserBrowserConnection,
                activeInternetDownTime
            );
            if (routeMatch) {
                enqueueSnackbar(t('internetConnection.restored'), {
                    variant: 'success',
                });
            }
        }

        toggleErrorModal({ isVisible: false });
        AccountManager.removeItem('activeInternetDownTime');

        if (routeMatch?.shouldRefresh) {
            window.location.reload();
        }
    }

    /**
     * set up event listeners
     */
    function addEventListeners() {
        window.addEventListener('visibilitychange', onVisibilitychange);

        window.addEventListener('online', onBrowserOnline);

        window.addEventListener('offline', onBrowserOffline);
    }

    /**
     * remove event listeners
     */
    function removeEventListeners() {
        if (window.navigator.onLine) {
            onBrowserOnline();
        }

        window.removeEventListener('visibilitychange', onVisibilitychange);

        window.removeEventListener('online', onBrowserOnline);

        window.removeEventListener('offline', onBrowserOffline);
    }

    return null;
}
